import React from 'react';

import type {BottomBarModel, BottomTab} from '../lib/bottomBar.js';
import './BottomBar.css';
import Icon from './Icon.js';

// ============================================================================
// BottomBar — the phone bottom tab bar (Design Constitution N02, Q09, Table C)
// ----------------------------------------------------------------------------
// Draws the model from src/lib/bottomBar.ts. Under 640px only (index.html
// .wcf-bottom-bar): up to four tabs, each a line icon over a Title Case label,
// at least 44px tall; the active tab is brand green with aria-current="page",
// the others label gray (N02). 'More' opens a menu card of the remaining
// destinations (N03: 18px card, 44px rows, icon + label, gray wash), above the
// bar on the menus layer; ↑/↓/Home/End move, Esc closes it and returns focus
// to More (O06), a tap outside closes it. While the bar shows, index.html pads
// the page and lifts toasts by --bottom-bar-h.
// ============================================================================

type BottomBarProps = {
  model: BottomBarModel;
  onSelect: (tab: BottomTab) => void;
};

export function BottomBar({model, onSelect}: BottomBarProps) {
  const [moreOpen, setMoreOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLElement | null>(null);
  const moreRef = React.useRef<HTMLButtonElement | null>(null);
  const itemRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = React.useId();

  React.useEffect(() => {
    if (!moreOpen) return undefined;
    const onPointer = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
    };
  }, [moreOpen]);

  if (model.hidden) return null;
  const {tabs, more, activeKey} = model;

  const focusItem = (i: number) => {
    const n = more.length;
    if (n) itemRefs.current[((i % n) + n) % n]?.focus();
  };
  const openMore = (at: number) => {
    setMoreOpen(true);
    requestAnimationFrame(() => focusItem(at));
  };
  const closeMore = (returnFocus: boolean) => {
    setMoreOpen(false);
    if (returnFocus) moreRef.current?.focus();
  };
  const pick = (t: BottomTab) => {
    setMoreOpen(false);
    onSelect(t);
  };

  return (
    <nav ref={rootRef} className="wcf-bottom-bar" aria-label="Sections" data-bottom-bar={model.kind}>
      {tabs.map((t) => {
        const active = activeKey === t.key;
        return (
          <button
            key={t.key}
            type="button"
            className={'wcf-clickable wcf-bottom-bar__tab' + (active ? ' is-active' : '')}
            aria-current={active ? 'page' : undefined}
            data-bottom-tab={t.key}
            onClick={() => pick(t)}
          >
            <Icon name={t.icon} size={22} />
            <span className="wcf-bottom-bar__label">{t.label}</span>
          </button>
        );
      })}
      {more.length > 0 && (
        <button
          ref={moreRef}
          type="button"
          className={'wcf-clickable wcf-bottom-bar__tab' + (activeKey === 'more' ? ' is-active' : '')}
          aria-current={activeKey === 'more' ? 'page' : undefined}
          aria-haspopup="menu"
          aria-expanded={moreOpen}
          aria-controls={moreOpen ? menuId : undefined}
          data-bottom-tab="more"
          onClick={() => (moreOpen ? closeMore(false) : openMore(0))}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              openMore(more.length - 1);
            }
          }}
        >
          <Icon name="more" size={22} />
          <span className="wcf-bottom-bar__label">More</span>
        </button>
      )}
      {moreOpen && (
        <div
          id={menuId}
          role="menu"
          aria-label="More"
          className="wcf-bottom-bar__menu"
          data-bottom-more="1"
          onKeyDown={(e) => {
            const at = itemRefs.current.findIndex((el) => el === document.activeElement);
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              closeMore(true);
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              focusItem(at + 1);
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              focusItem(at - 1);
            } else if (e.key === 'Home') {
              e.preventDefault();
              focusItem(0);
            } else if (e.key === 'End') {
              e.preventDefault();
              focusItem(more.length - 1);
            } else if (e.key === 'Tab') {
              closeMore(false);
            }
          }}
        >
          {more.map((t, i) => (
            <button
              key={t.key}
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className="wcf-bottom-bar__item"
              data-bottom-more-item={t.key}
              onClick={() => pick(t)}
            >
              <Icon name={t.icon} size={20} />
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      )}
    </nav>
  );
}

export default BottomBar;
